import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, updateProfile, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './Firebase';
import {
  saveQuizResult, getProgression, logStudyTime, getStudyTime, isUnlocked
} from './Firestoreservice';
import {
  BookOpen, Brain, TrendingUp, LogOut, Play, ChevronRight,
  ChevronDown, Clock, CheckCircle, XCircle, Award,
  User, Menu, X, RefreshCw, GraduationCap, BarChart3,
  Phone, Mail, MapPin, MessageCircle, Lock, Circle, Star,
  Eye, EyeOff
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';
import logo from './safi1.jpeg';
import safi11 from './safi11.webp';
import safi12 from './safi12.jpg';
import safi13 from './safi13.jpg';
import rsWeek1  from './audio/rs-week1.mp3';
import rsWeek2  from './audio/rs-week2.mp3';
import rsWeek3  from './audio/rs-week3.mp3';
import rsWeek4  from './audio/rs-week4.mp3';
import rsWeek5  from './audio/rs-week5.mp3';
import rsWeek6  from './audio/rs-week6.mp3';
import rsWeek7  from './audio/rs-week7.mp3';
import rsWeek8  from './audio/rs-week8.mp3';
import rsWeek9  from './audio/rs-week9.mp3';
import rsWeek10 from './audio/rs-week10.mp3';
import rsWeek11 from './audio/rs-week11.mp3';
import rsWeek12 from './audio/rs-week12.mp3';
import safi14 from './safi14.png';
import safi15 from './safi15.jpg';
import safi16 from './safi16.webp';
import './Dashboard.css';
import { COURSES_DATA as COURSES_BY_LEVEL, QUIZ_DATA } from './AdminData';

const AUDIO_FILES = {
  'a1-rs1':  rsWeek1,  'a1-rs2':  rsWeek2,  'a1-rs3':  rsWeek3,
  'a1-rs4':  rsWeek4,  'a1-rs5':  rsWeek5,  'a1-rs6':  rsWeek6,
  'a1-rs7':  rsWeek7,  'a1-rs8':  rsWeek8,  'a1-rs9':  rsWeek9,
  'a1-rs10': rsWeek10, 'a1-rs11': rsWeek11, 'a1-rs12': rsWeek12,
};

const CATEGORY_IMAGES = {
  'safi12': safi12,
  'safi13': safi13,
  'safi14': safi14,
};

// ─── DONNÉES DES COURS ───────────────────────────────────────────────────────

// ─── COMPOSANT PRINCIPAL ─────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser]           = useState(null);
  const [etudiant, setEtudiant]   = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading]     = useState(true);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [scrolled, setScrolled]   = useState(false);
  const [progression, setProgression] = useState({});
  const [studyTime, setStudyTime]     = useState([]);

  useEffect(() => {
    document.title = 'SAFI Academia — Espace Étudiant';
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { navigate('/connexion'); return; }
      setUser(u);
      const snap = await getDoc(doc(db, 'etudiants', u.uid));
      if (snap.exists()) setEtudiant(snap.data());
      const prog = await getProgression(u.uid);
      setProgression(prog);
      const temps = await getStudyTime(u.uid);
      setStudyTime(temps);
      setLoading(false);
      await logStudyTime(u.uid, 1);
    });
    return unsub;
  }, [navigate]);

  const refreshProgression = useCallback(async () => {
    if (!user) return;
    const prog = await getProgression(user.uid);
    setProgression(prog);
    const temps = await getStudyTime(user.uid);
    setStudyTime(temps);
  }, [user]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  if (loading) return (
    <div className="dash-loading">
      <img src={logo} alt="SAFI" className="dash-loading__logo" />
      <div className="dash-loading__spinner" />
    </div>
  );

  const niveau     = etudiant?.niveau || 'A1';
  const courseData = COURSES_BY_LEVEL[niveau] || COURSES_BY_LEVEL['A1'];

  return (
    <div className="dash">
      <header className={`dash-nav ${scrolled ? 'dash-nav--scrolled' : ''}`}>
        <div className="dash-nav__inner">
          <button className="dash-nav__brand" onClick={() => setActiveTab('home')}>
            <img src={logo} alt="SAFI Academia" />
            <span>SAFI Academia</span>
          </button>
          <nav className={`dash-nav__links ${menuOpen ? 'open' : ''}`}>
            {[
              { key: 'formations',  label: 'Mes formations', icon: <BookOpen size={16} /> },
              { key: 'quiz',        label: 'Quiz',           icon: <GraduationCap size={16} /> },
              { key: 'progression', label: 'Progression',    icon: <BarChart3 size={16} /> },
            ].map(t => (
              <button key={t.key}
                className={`dash-nav__tab ${activeTab === t.key ? 'active' : ''}`}
                onClick={() => { setActiveTab(t.key); setMenuOpen(false); }}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </nav>
          <div className="dash-nav__right">
            <button
              className={`dash-nav__compte ${activeTab === 'compte' ? 'active' : ''}`}
              onClick={() => { setActiveTab('compte'); setMenuOpen(false); }}
            >
              <div className="dash-nav__avatar"><User size={18} /></div>
              <div className="dash-nav__user-info">
                <span className="dash-nav__name">{etudiant?.nom?.split(' ')[0]}</span>
                <span className="dash-nav__niveau">Niveau {niveau}</span>
              </div>
            </button>
            <button className="dash-nav__logout" onClick={handleLogout}><LogOut size={18} /></button>
            <button className="dash-nav__burger" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <main className="dash-main">
        {activeTab === 'home'        && <TabHome etudiant={etudiant} niveau={niveau} setActiveTab={setActiveTab} progression={progression} />}
        {activeTab === 'formations'  && <TabFormations courseData={courseData} niveau={niveau} />}
        {activeTab === 'quiz'        && <TabQuiz courseData={courseData} niveau={niveau} uid={user?.uid} progression={progression} onQuizComplete={refreshProgression} />}
        {activeTab === 'progression' && <TabProgression etudiant={etudiant} niveau={niveau} progression={progression} studyTime={studyTime} />}
        {activeTab === 'compte'      && <TabCompte etudiant={etudiant} user={user} onUpdate={(data) => setEtudiant(prev => ({...prev, ...data}))} />}
      </main>

      <DashFooter logo={logo} setActiveTab={setActiveTab} />
    </div>
  );
}

// ─── ONGLET ACCUEIL ──────────────────────────────────────────────────────────
function TabHome({ etudiant, niveau, setActiveTab }) {
  const courseData = COURSES_BY_LEVEL[niveau] || COURSES_BY_LEVEL['A1'];

  const categoryInfo = {
    'Grammar': {
      color: '#1B2B5E',
      sub: 'Règles & Structures',
      detail: 'Maîtrisez les fondamentaux de la langue anglaise : pronoms, articles, adjectifs et structure des phrases. Une base solide indispensable pour progresser.',
      count: courseData.categories['Grammar']?.courses.length || 0,
    },
    'Conjugating': {
      color: '#2a3f80',
      sub: 'Verbes & Temps',
      detail: 'Apprenez à conjuguer les verbes anglais au présent, passé et futur. De "to be" aux temps complexes, maîtrisez chaque forme verbale étape par étape.',
      count: courseData.categories['Conjugating']?.courses.length || 0,
    },
    'Reading & Speaking': {
      color: '#111d42',
      sub: 'Lecture & Expression orale',
      detail: 'Développez votre compréhension de textes anglais et votre aisance à l\'oral. Dialoguez, lisez et exprimez-vous avec confiance dans des situations réelles.',
      count: courseData.categories['Reading & Speaking']?.courses.length || 0,
    },
  };

  return (
    <>
      {/* Hero */}
      <div className="dash-hero">
        <img src={safi11} alt="SAFI Academia" className="dash-hero__bg" />
        <div className="dash-hero__overlay" />
        <div className="dash-hero__content">
          <p className="dash-hero__eyebrow">✦ Bienvenue sur votre espace</p>
          <h1 className="dash-hero__title">
            Bonjour, <span>{etudiant?.nom?.split(' ')[0]} 👋</span>
          </h1>
          <p className="dash-hero__sub">
            Vous êtes en niveau <strong>{niveau}</strong>. Continuez à progresser — chaque leçon vous rapproche de vos objectifs.
          </p>
          <div className="dash-hero__btns">
            <button className="btn-gold" onClick={() => setActiveTab('formations')}>
              <BookOpen size={16} /> Mes formations
            </button>
            <button className="btn-outline" onClick={() => setActiveTab('progression')}>
              <BarChart3 size={16} /> Ma progression
            </button>
          </div>
        </div>
      </div>

      {/* Section catégories */}
      <div className="dash-courses-section">
        <div className="dash-courses-section__header">
          <h2>Mes formations en cours</h2>
          <button className="dash-courses-section__all" onClick={() => setActiveTab('formations')}>
            Voir tout <ChevronRight size={15} />
          </button>
        </div>

        <div className="dash-cat-grid">
          {Object.entries(courseData.categories).map(([cat, catData]) => {
            const info = categoryInfo[cat] || { color: '#1B2B5E', sub: '', detail: '', count: catData.courses.length };
            return (
              <div key={cat} className="dash-cat-card" onClick={() => setActiveTab('formations')}>
                {/* Image fond */}
                <div className="dash-cat-card__img-wrap">
                  <img src={CATEGORY_IMAGES[catData.image] || catData.image} alt={cat} className="dash-cat-card__img" />
                  <div className="dash-cat-card__overlay" />
                  <div className="dash-cat-card__top">
                    <span className="dash-cat-card__badge">{cat}</span>
                    <span className="dash-cat-card__play"><Play size={20} fill="white" /></span>
                  </div>
                  <p className="dash-cat-card__sub">{info.sub}</p>
                </div>
                {/* Corps */}
                <div className="dash-cat-card__body">
                  <div className="dash-cat-card__meta">
                    <GraduationCap size={13} />
                    <span>SAFI Academia</span>
                    <span className="dot">·</span>
                    <BookOpen size={13} />
                    <span>{info.count} cours</span>
                  </div>
                  <h3 className="dash-cat-card__title">{cat}</h3>
                  <p className="dash-cat-card__detail">{info.detail}</p>
                  <div className="dash-cat-card__footer">
                    <Clock size={13} />
                    <span>Niveau {niveau}</span>
                    <span className="dash-cat-card__cta">Commencer <ChevronRight size={13} /></span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ─── ONGLET FORMATIONS ───────────────────────────────────────────────────────
// ─── LECTEUR AUDIO MP3 STYLE WHATSAPP ────────────────────────────────────────
function AudioButton({ courseId }) {
  const [playing, setPlaying]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [elapsed, setElapsed]   = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef                = React.useRef(null);

  const src = AUDIO_FILES[courseId];
  if (!src) return null;

  const fmt = (s) => {
    const m   = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2,'0')}`;
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else         { audio.play();  setPlaying(true);  }
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;
    setElapsed(audio.currentTime);
    setProgress((audio.currentTime / audio.duration) * 100 || 0);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect        = e.currentTarget.getBoundingClientRect();
    const pct         = Math.max(0, Math.min((e.clientX - rect.left) / rect.width, 1));
    audio.currentTime = pct * audio.duration;
    setProgress(pct * 100);
  };

  const handleEnded = () => { setPlaying(false); setProgress(0); setElapsed(0); };

  const bars = [3,5,8,12,7,4,9,14,6,3,10,8,5,12,9,4,7,11,6,3,8,13,7,4,9,5,11,6,3,8,12,7,4,9,5,10];

  // Un seul temps affiché, comme sur WhatsApp : la durée totale au repos,
  // le temps écoulé pendant la lecture. Plus compact et plus sobre.
  const displayedTime = (playing || progress > 0) ? fmt(elapsed) : fmt(duration);

  return (
    <div className="audio-player">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={handleEnded}
      />
      <button className="audio-player__btn" onClick={handlePlayPause}>
        {playing
          ? <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
          : <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><polygon points="5,3 19,12 5,21"/></svg>
        }
      </button>
      <div className="audio-player__wave-wrap" onClick={handleSeek}>
        <div className="audio-player__bars">
          {bars.map((h, i) => (
            <div key={i}
              className="audio-player__bar"
              style={{height:`${h}px`}}
            />
          ))}
        </div>
        <div className="audio-player__cursor" style={{left:`${progress}%`}}/>
      </div>
      <div className="audio-player__times">
        <span>{displayedTime}</span>
      </div>
    </div>
  );
}

// ─── ONGLET FORMATIONS ───────────────────────────────────────────────────────
function TabFormations({ courseData, niveau }) {
  const [openCats, setOpenCats] = useState({ [Object.keys(courseData.categories)[0]]: true });
  const [activeCourse, setActiveCourse] = useState(
    Object.values(courseData.categories)[0].courses[0]
  );

  const toggleCat = (cat) =>
    setOpenCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const renderContent = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let tableRows = [];
    let listItems = [];
    let i = 0;

    const flushTable = () => {
      if (tableRows.length > 0) {
        const headers = tableRows[0];
        const rows = tableRows.slice(2);
        elements.push(
          <div key={`tbl-${i}`} className="course-table-wrap">
            <table className="course-table">
              <thead><tr>{headers.split('|').filter((_,ci,a) => ci > 0 && ci < a.length-1).map((h,ci) => <th key={ci}>{h.trim()}</th>)}</tr></thead>
              <tbody>{rows.filter(r => r.trim() && !r.match(/^[\s|:-]+$/)).map((row,ri) => <tr key={ri}>{row.split('|').filter((_,ci,a) => ci > 0 && ci < a.length-1).map((cell,ci) => <td key={ci} dangerouslySetInnerHTML={{__html: cell.trim().replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>').replace(/\*(.+?)\*/g,'<em>$1</em>')}} />)}</tr>)}</tbody>
            </table>
          </div>
        );
        tableRows = [];
      }
    };

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(<ul key={`ul-${i}`} className="course-list">{listItems}</ul>);
        listItems = [];
      }
    };

    while (i < lines.length) {
      const line = lines[i];
      if (line.trim().startsWith('|')) { flushList(); tableRows.push(line); i++; continue; }
      else { flushTable(); }
      if (line.startsWith('## ')) { flushList(); elements.push(<div key={i} className="course-section-title"><h2>{line.replace('## ','')}</h2></div>); i++; continue; }
      if (line.startsWith('### ')) { flushList(); elements.push(<h3 key={i} className="course-sub-title">{line.replace('### ','')}</h3>); i++; continue; }
      if (line.startsWith('- ')) { listItems.push(<li key={i} dangerouslySetInnerHTML={{__html: line.replace('- ','').replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>').replace(/\*(.+?)\*/g,'<em>$1</em>')}} />); i++; continue; }
      else { flushList(); }
      if (line.startsWith('> ')) { elements.push(<blockquote key={i} className="course-quote" dangerouslySetInnerHTML={{__html: line.replace('> ','').replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>').replace(/\*(.+?)\*/g,'<em>$1</em>')}} />); i++; continue; }
      if (line.trim() === '') { i++; continue; }
      elements.push(<p key={i} className="course-para" dangerouslySetInnerHTML={{__html: line.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>').replace(/\*(.+?)\*/g,'<em>$1</em>').replace(/`(.+?)`/g,'<code>$1</code>')}} />);
      i++;
    }
    flushTable(); flushList();
    return elements;
  };
  return (
    <div className="formations-full">

      {/* Image hero */}
      <div className="formations-content__hero">
        <img src={safi15} alt="Cours" className="formations-content__hero-img" />
        <div className="formations-content__hero-overlay" />
        <div className="formations-content__hero-text">
          <span className="formations-content__hero-cat">
            {Object.entries(courseData.categories).find(([,v]) => v.courses.some(c => c.id === activeCourse?.id))?.[0]}
          </span>
          <h2 className="formations-content__hero-title">{activeCourse?.title}</h2>
          <span className="formations-content__hero-dur"><Clock size={14} /> {activeCourse?.duration}</span>
        </div>
      </div>

      {/* Modules accordéon + cours */}
      <div className="formations-full__body">
        <div className="formations-full__sidebar">
          {Object.entries(courseData.categories).map(([cat, catData], catIdx) => (
            <div key={cat} className="sidebar-module">
              <button
                className={`sidebar-module__header ${openCats[cat] ? 'open' : ''}`}
                onClick={() => toggleCat(cat)}
              >
                <div className="sidebar-module__info">
                  <span className="sidebar-module__num">Module {catIdx + 1}</span>
                  <span className="sidebar-module__name">{cat}</span>
                </div>
                <ChevronDown size={16} className="sidebar-module__arrow" />
              </button>
              {openCats[cat] && (
                <div className="sidebar-module__courses">
                  {catData.courses.map((course, idx) => (
                    <button
                      key={course.id}
                      className={`sidebar-course ${activeCourse?.id === course.id ? 'active' : ''}`}
                      onClick={() => setActiveCourse(course)}
                    >
                      <span className="sidebar-course__num">{catIdx + 1}.{idx + 1}</span>
                      <span className="sidebar-course__title">{course.title}</span>
                      <span className="sidebar-course__dur"><Clock size={11} /> {course.duration}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="formations-full__content">
          {activeCourse ? (
            <div className="course-reader">
              <div className="course-reader__objective">
                <span className="course-reader__objective-label">Objectif</span>
                <p>Maîtriser ce cours vous permettra de progresser et de valider votre quiz.</p>
              </div>
              {(activeCourse.id?.startsWith('a1-rs') || activeCourse.id?.startsWith('a2-rs')) && (
                <AudioButton courseId={activeCourse.id} />
              )}
              <div className="course-reader__content">
                {renderContent(activeCourse.content)}
              </div>
            </div>
          ) : (
            <p style={{padding:'2rem',color:'var(--text-muted)'}}>Sélectionnez un cours.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// garde CourseDetail pour compatibilité
function CourseDetail({ course, onBack }) {
  const [speaking, setSpeaking] = useState(false);

  const getPlainText = (text) => {
    return text
      .replace(/##+ /g, '')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/\*(.+?)\*/g, '$1')
      .replace(/> /g, '')
      .replace(/\| .+/g, '')
      .replace(/- /g, '')
      .replace(/^\d+\. /gm, '')
      .trim();
  };

  const handleSpeak = () => {
    if (speaking) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
      return;
    }
    const text = getPlainText(course.content);
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 0.85;
    utterance.pitch = 1;
    // Choisir une voix anglaise si disponible
    const voices = window.speechSynthesis.getVoices();
    const enVoice = voices.find(v => v.lang.startsWith('en'));
    if (enVoice) utterance.voice = enVoice;
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
    setSpeaking(true);
  };

  const renderContent = (text) =>
    text.split('\n').map((line, i) => {
      if (line.startsWith('## '))  return <h2 key={i}>{line.replace('## ','')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i}>{line.replace('### ','')}</h3>;
      if (line.startsWith('| '))   return <p key={i} className="table-line">{line}</p>;
      if (line.startsWith('- '))   return <li key={i}>{line.replace('- ','')}</li>;
      if (line.startsWith('> '))   return <blockquote key={i}>{line.replace('> ','')}</blockquote>;
      if (line.trim() === '')      return <br key={i} />;
      return <p key={i} dangerouslySetInnerHTML={{__html: line.replace(/\*\*(.+?)\*\*/g,'<strong>$1</strong>').replace(/\*(.+?)\*/g,'<em>$1</em>')}} />;
    });

  return (
    <div className="dash-section">
      <div style={{display:'flex',alignItems:'center',gap:'1rem',marginBottom:'1rem'}}>
        <button className="course-detail__back" onClick={onBack}>
          <ChevronRight size={16} style={{transform:'rotate(180deg)'}} /> Retour
        </button>
        {course.audio && (
          <button
            onClick={handleSpeak}
            style={{
              display:'flex',alignItems:'center',gap:'0.5rem',
              padding:'0.5rem 1.1rem',
              background: speaking ? '#e03131' : '#1B2B5E',
              color:'#fff',border:'none',borderRadius:8,
              fontSize:'0.85rem',fontWeight:600,cursor:'pointer',
              fontFamily:'inherit',transition:'background 0.2s',
            }}
          >
            {speaking ? '⏹ Arrêter' : '🔊 Écouter le texte'}
          </button>
        )}
      </div>
      <div className="course-detail__content">{renderContent(course.content)}</div>
    </div>
  );
}

// ─── ONGLET QUIZ ─────────────────────────────────────────────────────────────
// ─── DONNÉES QUIZ ─────────────────────────────────────────────────────────────
// Structure : quiz par cours (10 questions, seuil 5/10)
//             quiz module (35 questions, seuil 50%)
//             quiz final A1 (120 questions, seuil 75%)

// ─── ONGLET QUIZ ─────────────────────────────────────────────────────────────
function TabQuiz({ courseData, niveau, uid, progression, onQuizComplete }) {
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [current, setCurrent]       = useState(0);
  const [chosen, setChosen]         = useState(null);
  const [answers, setAnswers]       = useState([]);
  const [finished, setFinished]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const catList    = Object.keys(courseData.categories);
  const allCourses = catList.flatMap(cat =>
    courseData.categories[cat].courses.map((c, i) => ({
      ...c, cat,
      moduleCoursesIds: courseData.categories[cat].courses.map(x => x.id),
    }))
  );

  // Construit la liste globale des IDs cours dans l'ordre
  const globalCourseIds = allCourses.map(c => c.id);

  const checkUnlocked = (type, id, cat) => {
    const moduleIds = courseData.categories[cat]?.courses.map(c => c.id) || [];
    const allModIds = catList.map(c => `mod_${c}`);
    return isUnlocked({
      quizId: id, type,
      moduleCoursesIds: type === 'course'
        ? globalCourseIds
        : type === 'module' ? moduleIds : [],
      allModulesIds: allModIds,
      progression,
    });
  };

  const startQuiz = (type, id, data, cat = '') => {
    setActiveQuiz({ type, id, data, cat });
    setCurrent(0); setChosen(null); setAnswers([]);
    setFinished(false); setLastResult(null);
  };

  const handleAnswer = (idx) => { if (chosen !== null) return; setChosen(idx); };

  const handleNext = async () => {
    const q = activeQuiz.data.questions[current];
    const newAnswers = [...answers, {
      questionIndex: current,
      chosen,
      correct: chosen === q.a,
    }];

    if (current + 1 >= activeQuiz.data.questions.length) {
      // Fin du quiz — sauvegarde dans Firestore
      setSaving(true);
      const score     = newAnswers.filter(a => a.correct).length;
      const threshold = activeQuiz.type === 'final' ? 75 : 50;
      const result    = await saveQuizResult({
        uid,
        quizId:    activeQuiz.id,
        type:      activeQuiz.type,
        niveau,
        titre:     activeQuiz.data.title,
        score,
        total:     newAnswers.length,
        answers:   newAnswers,
        threshold,
      });
      setLastResult({ ...result, score, total: newAnswers.length, threshold });
      setAnswers(newAnswers);
      setSaving(false);
      setFinished(true);
      await onQuizComplete(); // rafraîchit la progression depuis Firestore
    } else {
      setAnswers(newAnswers);
      setCurrent(current + 1);
      setChosen(null);
    }
  };

  if (activeQuiz) {
    if (saving) return (
      <div className="quiz-layout">
        <div className="quiz-layout__sidebar" />
        <div className="quiz-layout__content" style={{display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{textAlign:'center',gap:'1rem',display:'flex',flexDirection:'column',alignItems:'center'}}>
            <div className="dash-loading__spinner" style={{borderTopColor:'var(--gold)'}} />
            <p style={{color:'var(--text-muted)',fontSize:'0.9rem'}}>Enregistrement de vos résultats…</p>
          </div>
        </div>
      </div>
    );

    if (finished && lastResult) {
      const { pct, passed, score, total, threshold } = lastResult;
      return (
        <div className="quiz-layout">
          <div className="quiz-layout__sidebar">
            <button className="quiz-back-btn" onClick={() => setActiveQuiz(null)}>
              <ChevronRight size={15} style={{transform:'rotate(180deg)'}} /> Retour
            </button>
          </div>
          <div className="quiz-layout__content">
            <div className="quiz-result">
              <div className={`quiz-result__circle ${passed ? 'pass' : 'fail'}`}>
                {passed ? <Award size={36}/> : <RefreshCw size={36}/>}
              </div>
              <h2>{passed ? 'Félicitations !' : 'Continuez vos efforts !'}</h2>
              <div className="quiz-result__score">{pct}%</div>
              <p>{score} bonne{score>1?'s':''} réponse{score>1?'s':''} sur {total}</p>
              <p className={passed ? 'text-success' : 'text-error'}>
                {passed
                  ? activeQuiz.type === 'final'
                    ? '✓ Examen validé — niveau complété ! Votre formateur sera notifié.'
                    : '✓ Validé et enregistré — le quiz suivant est débloqué !'
                  : `✗ Score requis : ${threshold}%. Révisez le cours et réessayez.`
                }
              </p>
              <div className="quiz-result__actions">
                <button className="btn-ghost" onClick={() => setActiveQuiz(null)}>← Retour aux quiz</button>
                <button className="btn-gold" onClick={() => startQuiz(activeQuiz.type, activeQuiz.id, activeQuiz.data, activeQuiz.cat)}>
                  <RefreshCw size={14}/> Recommencer
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const q     = activeQuiz.data.questions[current];
    const total = activeQuiz.data.questions.length;
    return (
      <div className="quiz-layout">
        <div className="quiz-layout__sidebar quiz-layout__sidebar--active">
          <button className="quiz-back-btn" onClick={() => setActiveQuiz(null)}>
            <ChevronRight size={15} style={{transform:'rotate(180deg)'}} /> Quitter
          </button>
          <div className="quiz-sidebar-info">
            <p className="quiz-sidebar-info__title">{activeQuiz.data.title}</p>
            <div className="quiz-sidebar-progress">
              <div className="quiz-sidebar-progress__bar" style={{width:`${(current/total)*100}%`}} />
            </div>
            <p className="quiz-sidebar-info__counter">{current+1} / {total}</p>
            <div className="quiz-sidebar-dots">
              {Array.from({length: Math.min(total,20)}).map((_,i) => {
                const a = answers[i];
                return <span key={i} className={`quiz-dot ${a ? (a.correct?'ok':'fail') : (i===current?'current':'')}`} />;
              })}
              {total > 20 && <span className="quiz-dot-more">+{total-20}</span>}
            </div>
          </div>
        </div>
        <div className="quiz-layout__content">
          <div className="quiz-active">
            <p className="quiz-active__counter">Question {current+1} / {total}</p>
            <h3 className="quiz-active__question">{q.q}</h3>
            <div className="quiz-active__options">
              {q.opts.map((opt, i) => {
                let cls = 'quiz-option';
                if (chosen !== null) {
                  if (i === q.a)         cls += ' correct';
                  else if (i === chosen) cls += ' wrong';
                }
                return (
                  <button key={i} className={cls} onClick={() => handleAnswer(i)}>
                    <span className="quiz-option__letter">{['A','B','C','D'][i]}</span>
                    {opt}
                    {chosen !== null && i === q.a     && <CheckCircle size={16} className="quiz-option__check"/>}
                    {chosen !== null && i === chosen && i !== q.a && <XCircle size={16} className="quiz-option__x"/>}
                  </button>
                );
              })}
            </div>
            {chosen !== null && (
              <button className="btn-gold quiz-active__next" onClick={handleNext}>
                {current+1 >= total ? 'Terminer & Enregistrer' : 'Question suivante'} <ChevronRight size={16}/>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }


  // ── ÉCRAN D'ACCUEIL DU QUIZ ──────────────────────────────────────────────
  const getScore     = (id) => progression[id]?.bestPct ?? null;
  const isPassed     = (id, thr=50) => (progression[id]?.bestPct ?? 0) >= thr;
  const isFinalUnlocked = () => catList.every(cat => isPassed(`mod_${cat}`));

  return (
    <div className="quiz-full">
      {/* Hero image */}
      <div className="quiz-hero-full">
        <img src={safi16} alt="Quiz" className="quiz-hero-full__img"/>
        <div className="quiz-hero-full__overlay"/>
        <div className="quiz-hero-full__text">
          <p className="quiz-hero__eyebrow">✦ Testez vos connaissances</p>
          <h2 className="quiz-hero__title">Quiz — Niveau {niveau}</h2>
          <p className="quiz-hero__sub">Complétez les quiz de chaque cours, puis le quiz du module avant d'accéder à l'examen final.</p>
        </div>
      </div>

      {/* Corps — liste des quiz + règles */}
      <div className="quiz-full__body">
        {/* Navigation quiz */}
        <div className="quiz-full__nav">
          <div className="quiz-sidebar-legend" style={{padding:'0 0 1rem'}}>
            <span className="ql ql--ok" /> Réussi
            <span className="ql ql--fail" /> Échoué
            <span className="ql ql--lock" /> Bloqué
          </div>
          {catList.map((cat, ci) => {
            const catCourses  = courseData.categories[cat].courses;
            const modId       = `mod_${cat}`;
            const modPassed   = isPassed(modId);
            const modScore    = getScore(modId);
            const modUnlocked = catCourses.every(c => isPassed(c.id));
            const catCourseIds = catCourses.map(c => c.id); // IDs dans CE module seulement
            return (
              <div key={cat} className="quiz-sidebar-module">
                <p className="quiz-sidebar-module__label">Module {ci+1} — {cat}</p>
                {catCourses.map((c) => {
                  const idxInCat = catCourseIds.indexOf(c.id);
                  // Premier quiz du module toujours débloqué, les suivants nécessitent le précédent
                  const unlocked = idxInCat === 0 || isPassed(catCourseIds[idxInCat - 1]);
                  const sc       = getScore(c.id);
                  const passed   = isPassed(c.id);
                  const quizData = QUIZ_DATA.courses[c.id];
                  return (
                    <button key={c.id}
                      className={`quiz-nav-item ${!unlocked?'locked':''} ${sc!==null?(passed?'done':'failed'):''}`}
                      disabled={!unlocked || !quizData}
                      onClick={() => quizData && startQuiz('course', c.id, quizData, cat)}
                    >
                      {!unlocked ? <Lock size={12}/> : passed ? <CheckCircle size={12}/> : <Circle size={12}/>}
                      <span>{c.title}</span>
                      {sc !== null && <span className="quiz-nav-item__score">{sc}%</span>}
                    </button>
                  );
                })}
                <button
                  className={`quiz-nav-item quiz-nav-item--module ${!modUnlocked?'locked':''} ${modScore!==null?(modPassed?'done':'failed'):''}`}
                  disabled={!modUnlocked}
                  onClick={() => modUnlocked && startQuiz('module', modId, QUIZ_DATA.modules[cat], cat)}
                >
                  {!modUnlocked ? <Lock size={12}/> : modPassed ? <CheckCircle size={12}/> : <GraduationCap size={12}/>}
                  <span>Quiz du Module</span>
                  {modScore !== null && <span className="quiz-nav-item__score">{modScore}%</span>}
                </button>
              </div>
            );
          })}
          <div className="quiz-sidebar-module">
            <p className="quiz-sidebar-module__label">Examen Final</p>
            <button
              className={`quiz-nav-item quiz-nav-item--final ${!isFinalUnlocked()?'locked':''} ${getScore('final_A1')!==null?(isPassed('final_A1',75)?'done':'failed'):''}`}
              disabled={!isFinalUnlocked()}
              onClick={() => isFinalUnlocked() && startQuiz('final','final_A1', QUIZ_DATA.final.A1)}
            >
              {!isFinalUnlocked() ? <Lock size={12}/> : isPassed('final_A1',75) ? <Award size={12}/> : <Star size={12}/>}
              <span>Examen {niveau} — 120 questions</span>
              {getScore('final_A1') !== null && <span className="quiz-nav-item__score">{getScore('final_A1')}%</span>}
            </button>
          </div>
        </div>

        {/* Règles */}
        <div className="quiz-full__rules">
          <h3>Comment ça fonctionne</h3>
          <div className="quiz-rule"><CheckCircle size={18} color="#2f9e44"/><div><strong>Quiz de cours</strong><span>10 questions · Seuil : 50% pour débloquer le suivant</span></div></div>
          <div className="quiz-rule"><GraduationCap size={18} color="#1B2B5E"/><div><strong>Quiz de module</strong><span>35 questions · Seuil : 50% pour débloquer le module suivant</span></div></div>
          <div className="quiz-rule"><Award size={18} color="#C9A84C"/><div><strong>Examen final {niveau}</strong><span>120 questions · Seuil : 75% pour valider le niveau</span></div></div>
          <div className="quiz-rule"><Lock size={18} color="#e03131"/><div><strong>Quiz bloqués</strong><span>Réussissez le quiz précédent pour débloquer</span></div></div>
        </div>
      </div>
    </div>
  );
}

function TabProgression({ etudiant, niveau, progression, studyTime }) {
  // Construit les données depuis la progression Firestore
  const quizScores = Object.entries(progression)
    .filter(([, v]) => v.type === 'course')
    .map(([id, v]) => ({ name: v.titre?.substring(0,15) || id, score: v.bestPct || 0, passed: v.passed }));

  const timeData = studyTime.length > 0
    ? studyTime.map(d => ({
        day: new Date(d.date).toLocaleDateString('fr-FR', {weekday:'short'}),
        min: d.minutes,
      }))
    : [
        { day: 'Lun', min: 0 }, { day: 'Mar', min: 0 }, { day: 'Mer', min: 0 },
        { day: 'Jeu', min: 0 }, { day: 'Ven', min: 0 }, { day: 'Sam', min: 0 }, { day: 'Dim', min: 0 },
      ];

  const passed    = quizScores.filter(q => q.passed).length;
  const total     = quizScores.length || 1;
  const avgScore  = total > 0 ? Math.round(quizScores.reduce((s,q)=>s+q.score,0)/total) : 0;
  const totalTime = timeData.reduce((s,d)=>s+d.min,0);
  const canExam   = avgScore >= 75;

  const moduleResults = Object.entries(progression)
    .filter(([, v]) => v.type === 'module')
    .map(([id, v]) => ({ name: id.replace('mod_',''), score: v.bestPct||0, passed: v.passed }));

  const finalResult = progression['final_A1'];

  const pieData = [
    { name: 'Réussis',  value: passed,        fill: '#2f9e44' },
    { name: 'À revoir', value: Math.max(0, total - passed), fill: '#e03131' },
  ];

  return (
    <div className="dash-section">
      <div className="dash-section__header">
        <h2><TrendingUp size={22} /> Ma progression</h2>
        <span className="niveau-badge">Niveau {niveau}</span>
      </div>

      {/* Alerte examen */}
      <div className={`exam-banner ${canExam ? 'exam-banner--ok' : 'exam-banner--nok'}`}>
        {canExam
          ? <><Award size={20} /> Vous êtes éligible à l'examen ! Score général : <strong>{avgScore}%</strong></>
          : <><XCircle size={20} /> Score général : <strong>{avgScore}%</strong> — Vous devez atteindre <strong>75%</strong> pour accéder à l'examen.</>
        }
      </div>

      {/* Stats rapides */}
      <div className="prog-stats">
        <div className="prog-stat">
          <span className="prog-stat__val">{avgScore}%</span>
          <span className="prog-stat__label">Score général</span>
        </div>
        <div className="prog-stat">
          <span className="prog-stat__val">{passed}/{total}</span>
          <span className="prog-stat__label">Quiz cours réussis</span>
        </div>
        <div className="prog-stat">
          <span className="prog-stat__val">{totalTime} min</span>
          <span className="prog-stat__label">Temps total</span>
        </div>
        <div className="prog-stat">
          <span className="prog-stat__val">{niveau}</span>
          <span className="prog-stat__label">Niveau actuel</span>
        </div>
      </div>

      {/* Graphiques */}
      <div className="prog-charts">

        {/* Bar chart — scores par quiz */}
        <div className="prog-chart-box">
          <h3>Scores par quiz</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={quizScores} margin={{top:10,right:10,bottom:10,left:0}}>
              <XAxis dataKey="name" tick={{fontSize:11}} />
              <YAxis domain={[0,100]} tick={{fontSize:11}} unit="%" />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="score" radius={[6,6,0,0]}>
                {quizScores.map((entry,i) => (
                  <Cell key={i} fill={entry.score >= 75 ? '#2f9e44' : '#e03131'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <p className="chart-legend">🟢 ≥ 75% validé &nbsp; 🔴 &lt; 75% à revoir</p>
        </div>

        {/* Pie chart — répartition */}
        <div className="prog-chart-box">
          <h3>Répartition des quiz</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value">
                {pieData.map((entry,i) => <Cell key={i} fill={entry.fill} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart — temps par jour */}
        <div className="prog-chart-box prog-chart-box--full">
          <h3>Temps d'étude cette semaine</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={timeData} margin={{top:10,right:10,bottom:10,left:0}}>
              <XAxis dataKey="day" tick={{fontSize:12}} />
              <YAxis tick={{fontSize:11}} unit=" min" />
              <Tooltip formatter={(v) => `${v} min`} />
              <Bar dataKey="min" fill="#1B2B5E" radius={[6,6,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Détail par quiz */}
      <div className="prog-quiz-detail">
        <h3>Quiz de cours</h3>
        {quizScores.length === 0 && <p style={{fontSize:'0.85rem',color:'var(--text-muted)',padding:'0.5rem 0'}}>Aucun quiz complété pour le moment.</p>}
        {quizScores.map((q,i) => (
          <div key={i} className="prog-quiz-row">
            <span className="prog-quiz-row__name">{q.name}</span>
            <div className="prog-quiz-row__bar-wrap">
              <div className="prog-quiz-row__bar" style={{width:`${q.score}%`, background: q.score>=50?'#2f9e44':'#e03131'}} />
            </div>
            <span className="prog-quiz-row__pct">{q.score}%</span>
            {q.score >= 50 ? <CheckCircle size={16} color="#2f9e44"/> : <XCircle size={16} color="#e03131"/>}
          </div>
        ))}
      </div>

      {/* Quiz modules */}
      {moduleResults.length > 0 && (
        <div className="prog-quiz-detail" style={{marginTop:'1.25rem'}}>
          <h3>Quiz de modules</h3>
          {moduleResults.map((m,i) => (
            <div key={i} className="prog-quiz-row">
              <span className="prog-quiz-row__name">{m.name}</span>
              <div className="prog-quiz-row__bar-wrap">
                <div className="prog-quiz-row__bar" style={{width:`${m.score}%`, background: m.score>=50?'#2f9e44':'#e03131'}} />
              </div>
              <span className="prog-quiz-row__pct">{m.score}%</span>
              {m.score >= 50 ? <CheckCircle size={16} color="#2f9e44"/> : <XCircle size={16} color="#e03131"/>}
            </div>
          ))}
        </div>
      )}

      {/* Examen final */}
      {finalResult && (
        <div className={`exam-banner ${finalResult.bestPct>=75?'exam-banner--ok':'exam-banner--nok'}`} style={{marginTop:'1.25rem'}}>
          <Award size={20}/>
          Examen final : <strong>{finalResult.bestPct}%</strong>
          {finalResult.bestPct >= 75
            ? ' — ✓ Niveau validé ! Votre formateur sera notifié pour passer au niveau suivant.'
            : ` — ✗ Score requis : 75%. Encore ${75 - finalResult.bestPct}% d'effort !`
          }
        </div>
      )}
    </div>
  );
}

// ─── ONGLET MON COMPTE ───────────────────────────────────────────────────────
function TabCompte({ etudiant, user, onUpdate }) {
  const [form, setForm]           = useState({ nom: etudiant?.nom || '', pays: etudiant?.pays || '' });
  const [pwdForm, setPwdForm]     = useState({ current: '', nouveau: '', confirm: '' });
  const [showPwd, setShowPwd]     = useState({ current: false, nouveau: false, confirm: false });
  const [saving, setSaving]       = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);
  const [msgInfo, setMsgInfo]     = useState('');
  const [msgPwd, setMsgPwd]       = useState('');
  const [errorInfo, setErrorInfo] = useState('');
  const [errorPwd, setErrorPwd]   = useState('');

  const set    = f => e => setForm(p => ({...p, [f]: e.target.value}));
  const setPwd = f => e => setPwdForm(p => ({...p, [f]: e.target.value}));

  const validatePwd = (pwd) => ({
    length:   pwd.length >= 8,
    upper:    /[A-Z]/.test(pwd),
    lower:    /[a-z]/.test(pwd),
    special:  /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd),
    noAccent: !/[àâäéèêëîïôöùûüç]/i.test(pwd),
  });
  const pwdRules = validatePwd(pwdForm.nouveau);
  const pwdValid = Object.values(pwdRules).every(Boolean);

  const handleSaveInfo = async () => {
    if (!form.nom.trim()) { setErrorInfo('Le nom est requis.'); return; }
    setSaving(true); setErrorInfo(''); setMsgInfo('');
    try {
      await updateDoc(doc(db, 'etudiants', user.uid), { nom: form.nom, pays: form.pays, updatedAt: serverTimestamp() });
      await updateProfile(user, { displayName: form.nom });
      onUpdate({ nom: form.nom, pays: form.pays });
      setMsgInfo('Informations mises à jour avec succès.');
    } catch { setErrorInfo('Erreur lors de la mise à jour. Réessayez.'); }
    finally  { setSaving(false); }
  };

  const handleChangePwd = async () => {
    setErrorPwd(''); setMsgPwd('');
    if (!pwdValid)                          { setErrorPwd('Le mot de passe ne respecte pas les critères.'); return; }
    if (pwdForm.nouveau !== pwdForm.confirm) { setErrorPwd('Les mots de passe ne correspondent pas.'); return; }
    setSavingPwd(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, pwdForm.current);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, pwdForm.nouveau);
      setMsgPwd('Mot de passe modifié avec succès.');
      setPwdForm({ current: '', nouveau: '', confirm: '' });
    } catch (e) {
      const msgs = { 'auth/wrong-password':'Mot de passe actuel incorrect.', 'auth/invalid-credential':'Mot de passe actuel incorrect.', 'auth/too-many-requests':'Trop de tentatives. Réessayez plus tard.' };
      setErrorPwd(msgs[e.code] || 'Erreur lors du changement. Réessayez.');
    } finally { setSavingPwd(false); }
  };

  const paysOptions = ['Sénégal','Gabon','Congo Brazza','Cameroun'];

  return (
    <div className="compte-page">
      <div className="compte-header">
        <div className="compte-header__avatar"><User size={36} /></div>
        <div>
          <h2 className="compte-header__name">{etudiant?.nom}</h2>
          <div className="compte-header__badges">
            <span className="compte-badge">Niveau {etudiant?.niveau}</span>
            <span className="compte-badge compte-badge--pays">{etudiant?.pays}</span>
            {etudiant?.presentiel && <span className="compte-badge compte-badge--presentiel">✓ Présentiel</span>}
          </div>
          <p className="compte-header__email">{user?.email}</p>
        </div>
      </div>

      <div className="compte-grid">
        {/* Infos personnelles */}
        <div className="compte-card">
          <h3 className="compte-card__title"><User size={16}/> Informations personnelles</h3>
          <div className="compte-field">
            <label>Nom complet</label>
            <input type="text" value={form.nom} onChange={set('nom')} placeholder="Votre nom complet" />
          </div>
          <div className="compte-field">
            <label>Adresse e-mail</label>
            <input type="email" value={user?.email || ''} disabled className="compte-input--disabled" />
            <span className="compte-hint">L'email ne peut pas être modifié.</span>
          </div>
          <div className="compte-field">
            <label>Date de naissance</label>
            <input type="date" value={etudiant?.dateNaissance || ''} disabled className="compte-input--disabled" />
          </div>
          <div className="compte-field">
            <label>Pays</label>
            <select value={form.pays} onChange={set('pays')}>
              {paysOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div className="compte-field">
            <label>Niveau actuel</label>
            <input value={`${etudiant?.niveau || 'A1'} — modifiable par l'admin uniquement`} disabled className="compte-input--disabled" />
          </div>
          {msgInfo   && <div className="compte-msg compte-msg--ok">✓ {msgInfo}</div>}
          {errorInfo && <div className="compte-msg compte-msg--err">✗ {errorInfo}</div>}
          <button className="compte-btn" onClick={handleSaveInfo} disabled={saving}>
            {saving ? <span className="spinner"/> : 'Enregistrer les modifications'}
          </button>
        </div>

        {/* Changer le mot de passe */}
        <div className="compte-card">
          <h3 className="compte-card__title"><Lock size={16}/> Changer le mot de passe</h3>
          {[['current','Mot de passe actuel'],['nouveau','Nouveau mot de passe'],['confirm','Confirmer le nouveau']].map(([key,label]) => (
            <div className="compte-field" key={key}>
              <label>{label}</label>
              <div className="compte-pwd-wrap">
                <input type={showPwd[key] ? 'text' : 'password'} value={pwdForm[key]} onChange={setPwd(key)} placeholder="••••••••" />
                <button type="button" className="compte-pwd-toggle" onClick={() => setShowPwd(p => ({...p, [key]: !p[key]}))}>
                  {showPwd[key] ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
            </div>
          ))}
          {pwdForm.nouveau.length > 0 && (
            <ul className="pwd-rules">
              {[[pwdRules.length,'Au moins 8 caractères'],[pwdRules.upper,'Une majuscule (A–Z)'],[pwdRules.lower,'Une minuscule (a–z)'],[pwdRules.special,'Un caractère spécial (!@#...)'],[pwdRules.noAccent,'Pas de caractères accentués']].map(([ok,label],i) => (
                <li key={i} className={ok ? 'ok' : ''}>
                  {ok ? <CheckCircle size={13} className="rule-icon rule-icon--ok"/> : <Circle size={13} className="rule-icon"/>}
                  {label}
                </li>
              ))}
            </ul>
          )}
          {msgPwd   && <div className="compte-msg compte-msg--ok">✓ {msgPwd}</div>}
          {errorPwd && <div className="compte-msg compte-msg--err">✗ {errorPwd}</div>}
          <button className="compte-btn" onClick={handleChangePwd} disabled={savingPwd}>
            {savingPwd ? <span className="spinner"/> : 'Mettre à jour le mot de passe'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FOOTER COMPLET ───────────────────────────────────────────────────────────
function DashFooter({ logo, setActiveTab }) {
  return (
    <footer className="dash-footer-full">

      {/* Topbar contact */}
      <div className="dash-footer-full__topbar">
        <div className="dash-footer-full__topbar-inner">
          <div className="dash-footer-full__contact-item">
            <Phone size={15} className="dfc-icon" />
            <div>
              <span className="dfc-label">Service client</span>
              <a href="tel:+221767498675" className="dfc-value">+221 76 749 86 75</a>
            </div>
          </div>
          <div className="dash-footer-full__divider" />
          <div className="dash-footer-full__contact-item">
            <Mail size={15} className="dfc-icon" />
            <div>
              <span className="dfc-label">Email</span>
              <a href="mailto:contact@safiacademia.org" className="dfc-value">contact@safiacademia.org</a>
            </div>
          </div>
          <div className="dash-footer-full__divider" />
          <div className="dash-footer-full__contact-item">
            <Clock size={15} className="dfc-icon" />
            <div>
              <span className="dfc-label">Disponibilité</span>
              <span className="dfc-value">Lun – Ven · 9h15 à 17h30</span>
            </div>
          </div>
        </div>
      </div>

      {/* Corps */}
      <div className="dash-footer-full__body">
        <div className="dash-footer-full__container">

          {/* Brand */}
          <div className="dash-footer-full__brand">
            <img src={logo} alt="SAFI Academia" />
            <p className="dfc-tagline">* Stand up and Act *</p>
            <p className="dfc-desc">Formez-vous à l'anglais et partez à l'international avec SAFI Academia.</p>
            <div className="dfc-flags">
              {[['sn','Sénégal'],['ga','Gabon'],['cm','Cameroun'],['cg','Congo']].map(([code, name]) => (
                <div key={code} className="dfc-flag-item">
                  <img src={`https://flagcdn.com/w40/${code}.png`} alt={name} />
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Liens */}
          <div className="dash-footer-full__links">
            <div className="dfc-col">
              <h4>Mon espace</h4>
              <button onClick={() => setActiveTab('formations')}><BookOpen size={13} /> Mes formations</button>
              <button onClick={() => setActiveTab('quiz')}><GraduationCap size={13} /> Quiz</button>
              <button onClick={() => setActiveTab('progression')}><BarChart3 size={13} /> Ma progression</button>
            </div>
            <div className="dfc-col">
              <h4>SAFI Academia</h4>
              <a href="/">Accueil</a>
              <a href="/#programmes">Programmes</a>
              <a href="/#niveaux">Niveaux</a>
              <a href="/#faq">FAQ</a>
            </div>
            <div className="dfc-col">
              <h4>Contact</h4>
              <a href="tel:+221767498675"><Phone size={12} /> +221 76 749 86 75</a>
              <a href="mailto:contact@safiacademia.org"><Mail size={12} /> contact@safiacademia.org</a>
              <a href="#"><MapPin size={12} /> Dakar, Sénégal</a>
              <a href="https://wa.me/221767498675" target="_blank" rel="noopener noreferrer"><MessageCircle size={12} /> WhatsApp</a>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom */}
      <div className="dash-footer-full__bottom">
        <p>© 2026 SAFI Academia. Tous droits réservés.</p>
        <div className="dfc-bottom-flags">
          {['sn','ga','cm','cg'].map(code => (
            <img key={code} src={`https://flagcdn.com/w20/${code}.png`} alt={code} />
          ))}
        </div>
      </div>

    </footer>
  );
}